import { SITE_URL } from '@/seo';
import type { Doctor } from '@/data/featuredDoctors';

const DOCTOR_SCHEMA_SCRIPT_ID = 'doctor-itemlist-schema';

type DoctorItemListSchemaOptions = {
    path: string;
    pageTitle: string;
    pageDescription: string;
    medicalSpecialty: string;
    doctors: Doctor[];
};

const normalizePath = (path: string): string => {
    if (!path)
        return '/';
    return path.startsWith('/') ? path : `/${path}`;
};

const buildDoctorAnchorId = (doctor: Doctor): string => {
    const fallback = doctor.name || 'doctor';
    const baseId = doctor.licenseNumber || fallback;
    return `doctor-${encodeURIComponent(baseId)}`;
};

export const buildDoctorItemListSchema = ({ path, pageTitle, pageDescription, medicalSpecialty, doctors, }: DoctorItemListSchemaOptions) => {
    const normalizedPath = normalizePath(path);
    const url = `${SITE_URL}${normalizedPath}`;
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": pageTitle,
        "description": pageDescription,
        "url": url,
        "numberOfItems": doctors.length,
        "itemListOrder": "https://schema.org/ItemListOrderAscending",
        "itemListElement": doctors.map((doctor, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "url": `${url}#${buildDoctorAnchorId(doctor)}`,
            "item": {
                "@type": "MedicalDoctor",
                "name": doctor.name,
                "description": doctor.description || doctor.specialty,
                "medicalSpecialty": medicalSpecialty,
                "identifier": doctor.licenseNumber
                    ? {
                        "@type": "PropertyValue",
                        "propertyID": "IR-MedicalCouncil",
                        "value": doctor.licenseNumber,
                    }
                    : undefined,
                "telephone": doctor.phones?.[0] || undefined,
                "address": doctor.address
                    ? {
                        "@type": "PostalAddress",
                        "streetAddress": doctor.address,
                        "addressLocality": "شهرقدس",
                        "addressRegion": "تهران",
                        "addressCountry": "IR",
                    }
                    : undefined,
                "image": doctor.image || undefined,
            },
        })),
    };
};

export const upsertDoctorItemListSchema = (schema: unknown) => {
    const existingScript = document.getElementById(DOCTOR_SCHEMA_SCRIPT_ID);
    if (existingScript) {
        existingScript.remove();
    }
    const script = document.createElement('script');
    script.id = DOCTOR_SCHEMA_SCRIPT_ID;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
        if (script.parentNode) {
            script.parentNode.removeChild(script);
        }
    };
};
